import React, { useState } from 'react';
import { ShieldCheck, Loader2, Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import { api, errMsg } from '@/lib/api';
import { Reveal } from './Motion';
import { Input } from '@/components/ui/input';

/**
 * Newsletter sign-up with a self-hosted "I'm not a robot" step:
 * ticking the box fetches a signed arithmetic challenge; the answer is verified server-side.
 */
export const NewsletterSection = () => {
  const { site } = useSite();
  const s = site?.settings || {};
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [robot, setRobot] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [hp, setHp] = useState('');

  if (s.newsletter_enabled === false) return null;

  const loadChallenge = async () => {
    try {
      const { data } = await api.get('/public/captcha');
      setChallenge(data);
      setAnswer('');
    } catch (e) {
      toast.error('Could not load verification. Please try again.');
      setRobot(false);
    }
  };

  const onRobot = async (checked) => {
    setRobot(checked);
    if (checked) await loadChallenge();
    else setChallenge(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error('Please enter your email address.');
    if (!robot || !challenge) return toast.error('Please confirm you are not a robot.');
    if (!answer.trim()) return toast.error('Please answer the verification question.');
    setBusy(true);
    try {
      await api.post('/public/subscribe', { email: email.trim(), name: name.trim(), captcha_token: challenge.token, captcha_answer: answer.trim(), website: hp });
      setDone(true);
      toast.success('Thank you for subscribing.');
    } catch (err) {
      toast.error(errMsg(err, 'Could not subscribe.'));
      await loadChallenge();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="section-y bg-[hsl(var(--brand-navy))] text-white noise" data-testid="newsletter-section">
      <div className="container-x relative z-[2] grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <Reveal><p className="eyebrow text-white/60">Newsletter</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-display text-3xl sm:text-4xl mt-4 leading-[1.1]">{s.newsletter_title || 'Stay in touch'}</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-5 text-white/75 text-[15px] leading-relaxed max-w-md">{s.newsletter_body || 'Seasonal offers and island stories, a few times a year.'}</p></Reveal>
        </div>
        <Reveal delay={0.1} className="md:col-span-6 md:col-start-7">
          {done ? (
            <div className="rounded-2xl bg-white/[0.06] border border-white/15 p-8 text-center" data-testid="newsletter-success">
              <span className="mx-auto h-12 w-12 rounded-full bg-[hsl(var(--brand-gold))] text-white inline-flex items-center justify-center"><Check className="h-6 w-6" /></span>
              <p className="font-display text-2xl mt-5">You are on the list</p>
              <p className="text-white/70 text-sm mt-2">We will write only when there is something worth sharing.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl bg-white/[0.06] border border-white/15 p-6 sm:p-8 space-y-4" data-testid="newsletter-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" aria-label="Name" className="h-12 bg-white/95 text-foreground border-0" data-testid="newsletter-name-input" />
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" aria-label="Email" className="h-12 bg-white/95 text-foreground border-0" data-testid="newsletter-email-input" />
              </div>
              {/* honeypot: hidden from humans */}
              <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="website" />

              <label className="flex items-center gap-3 rounded-xl bg-white/95 text-foreground px-4 py-3.5 cursor-pointer select-none" data-testid="newsletter-robot-label">
                <input type="checkbox" checked={robot} onChange={(e) => onRobot(e.target.checked)} className="h-5 w-5 accent-[hsl(var(--brand-teal))] cursor-pointer" data-testid="newsletter-robot-checkbox" />
                <span className="text-sm font-medium flex-1">I&apos;m not a robot</span>
                <ShieldCheck className="h-5 w-5 text-[hsl(var(--brand-teal))]" />
              </label>

              {robot && challenge && (
                <div className="rounded-xl bg-white/95 text-foreground px-4 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="newsletter-captcha">
                  <p className="text-sm flex-1"><span className="text-muted-foreground">Quick check:</span> <span className="font-medium" data-testid="newsletter-captcha-question">{challenge.question}</span></p>
                  <div className="flex items-center gap-2">
                    <Input inputMode="numeric" value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Answer" aria-label="Verification answer" className="h-10 w-28 bg-white" data-testid="newsletter-captcha-answer" />
                    <button type="button" onClick={loadChallenge} aria-label="New question" className="h-10 w-10 rounded-md border border-border inline-flex items-center justify-center text-muted-foreground hover:text-foreground" data-testid="newsletter-captcha-refresh"><RefreshCw className="h-4 w-4" /></button>
                  </div>
                </div>
              )}

              <button type="submit" disabled={busy || !robot} className="btn-gold w-full disabled:opacity-50 disabled:cursor-not-allowed" data-testid="newsletter-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{busy ? 'Subscribing...' : 'Subscribe'}
              </button>
              <p className="text-[11px] text-white/50 text-center">Unsubscribe any time. We never share your address.</p>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
